import { css } from 'styled-components';

/**
 * 滚动条样式工具 - CSS-in-JS 版本
 * 提供统一的滚动条隐藏和自定义样式
 */

// 基础的滚动条隐藏样式
export const hideScrollbar = css`
  /* 隐藏滚动条但保留滚动功能 */
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */

  &::-webkit-scrollbar {
    display: none; /* Chrome, Safari, Opera */
  }
`;

// 可滚动容器的基础样式
export const scrollableContainer = css`
  overflow: auto;
  ${hideScrollbar}
`;

// 自定义滚动条样式（细滚动条）
export const customScrollbar = css`
  /* For Webkit-based browsers (Chrome, Safari, Edge) */
  &::-webkit-scrollbar {
    width: 6px;
    height: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: rgba(255, 255, 255, 0.2);
    border-radius: 3px;
    transition: background-color 0.2s ease;

    &:hover {
      background-color: rgba(255, 255, 255, 0.3);
    }
  }

  /* For Firefox */
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.2) transparent;
`;

// 深色主题的滚动条样式
export const darkScrollbar = css`
  &::-webkit-scrollbar {
    width: 6px;
    height: 6px;
    background-color: transparent;
  }

  &::-webkit-scrollbar-track {
    background-color: rgba(0, 0, 0, 0.1);
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: rgba(100, 100, 100, 0.5);
    border-radius: 3px;
    transition: all 0.2s ease;

    &:hover {
      background-color: rgba(120, 120, 120, 0.7);
    }
  }

  /* Firefox */
  scrollbar-width: thin;
  scrollbar-color: rgba(100, 100, 100, 0.5) rgba(0, 0, 0, 0.1);
`;

// React Virtualized 组件的滚动条隐藏
export const hideVirtualizedScrollbar = css`
  /* 隐藏所有滚动条 */
  * {
    &::-webkit-scrollbar {
      display: none;
    }
    scrollbar-width: none;
    -ms-overflow-style: none;
  }

  /* 特别针对 react-virtualized 的组件 */
  .ReactVirtualized__List,
  .ReactVirtualized__Grid,
  .ReactVirtualized__AutoSizer {
    &::-webkit-scrollbar {
      display: none !important;
    }
    scrollbar-width: none !important;
    -ms-overflow-style: none !important;
  }
`;

// 主内容区域的滚动条样式
export const mainContentScrollbar = css`
  ${hideScrollbar}
`;

// Ant Design 组件的滚动条隐藏
export const hideAntdScrollbar = css`
  .ant-layout-content,
  .ant-drawer-content-wrapper,
  .ant-drawer-content,
  .ant-drawer-body {
    ${hideScrollbar}
  }
`;

// 全局滚动条样式重置（用于 createGlobalStyle）
export const globalScrollbarStyles = css`
  /* 全局滚动条样式 */
  * {
    scrollbar-width: thin;
    scrollbar-color: rgba(255, 255, 255, 0.2) transparent;
  }

  ::-webkit-scrollbar {
    width: 6px;
    height: 6px;
  }

  ::-webkit-scrollbar-track {
    background: transparent;
    border-radius: 3px;
  }

  ::-webkit-scrollbar-thumb {
    background-color: rgba(255, 255, 255, 0.2);
    border-radius: 3px;
    transition: background-color 0.2s ease;
  }

  ::-webkit-scrollbar-thumb:hover {
    background-color: rgba(255, 255, 255, 0.3);
  }

  /* 特定类名的滚动条隐藏 */
  .main-content-scrollable,
  .hide-scrollbar,
  .scrollable-container {
    ${hideScrollbar}
  }

  .scrollable-container {
    overflow: auto;
  }

  /* 匹配类名模式的滚动条隐藏 */
  [class*='MainContent'],
  [class*='ContentArea'],
  [class*='ContentContainer'] {
    ${hideScrollbar}
  }
`;

// 工具函数：创建带滚动条样式的组件
export const withScrollbar = (scrollbarType: 'hide' | 'custom' | 'dark' = 'hide') => {
  switch (scrollbarType) {
    case 'custom':
      return customScrollbar;
    case 'dark':
      return darkScrollbar;
    case 'hide':
    default:
      return hideScrollbar;
  }
};

// 导出常用的组合样式
export const scrollbarStyles = {
  hide: hideScrollbar,
  custom: customScrollbar,
  dark: darkScrollbar,
  container: scrollableContainer,
  virtualized: hideVirtualizedScrollbar,
  mainContent: mainContentScrollbar,
  antd: hideAntdScrollbar,
  global: globalScrollbarStyles,
};

export default scrollbarStyles;
